import { useState, useEffect, useRef } from 'react';
import useStore from '../store/store';
import './Fixtures.css';

function Fixtures() {
  const [activeSeason, setActiveSeason] = useState(null);
  const [activeView, setActiveView] = useState('group-stage');
  const [seasons, setSeasons] = useState([]);
  const [groupStages, setGroupStages] = useState(null);
  const [fixturesData, setFixturesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const originalBodyClass = useRef('');
  const seasonTabsRef = useRef(null);
  const [selectedBracket, setSelectedBracket] = useState(null);
  const [showBracketModal, setShowBracketModal] = useState(false);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [timelineGroupFilter, setTimelineGroupFilter] = useState('all');
  const [timelineTeamFilter, setTimelineTeamFilter] = useState('all');
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [matchPlayerStats, setMatchPlayerStats] = useState(null);
  const [loadingPlayerStats, setLoadingPlayerStats] = useState(false);
  const [allMatchesData, setAllMatchesData] = useState(null);

  // Function to get unique teams from matches
  const getUniqueTeams = (matches) => {
    const teamsMap = new Map();

    matches.forEach((match) => {
      if (match.attacker_team?.id && match.attacker_team?.name) {
        teamsMap.set(match.attacker_team.id, match.attacker_team);
      }
      if (match.defender_team?.id && match.defender_team?.name) {
        teamsMap.set(match.defender_team.id, match.defender_team);
      }
    });

    return Array.from(teamsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Scroll function for horizontal scrolling
  const scroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 300;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    // Store original body class and add no-bg class when Fixtures component mounts
    const bodyElement = document.querySelector('body');
    originalBodyClass.current = bodyElement.className;
    bodyElement.className = 'no-bg';

    // Restore original body class when component unmounts
    return () => {
      bodyElement.className = originalBodyClass.current;
    };
  }, []);

  useEffect(() => {
    // Subscribe to store changes for real-time updates
    const unsubscribe = useStore.subscribe((state) => {
      // Handle errors
      if (state.errors) {
        if (state.errors.seasons) {
          setError(`Failed to load seasons: ${state.errors.seasons}`);
          setLoading(false);
          return;
        }
        if (state.errors.fixtures) {
          console.error('Fixtures component: Fixtures loading error:', state.errors.fixtures);
        }
        if (state.errors.groupStages) {
          console.error('Fixtures component: Group stages loading error:', state.errors.groupStages);
        }
      }

      if (state.seasons && state.seasons.length > 0) {
        setSeasons(state.seasons);
        setError(null);

        // Use the current season from store or fallback to latest season
        const currentSeasonId = state.currentSeason?.id;
        if (currentSeasonId && state.seasons.find((s) => s.id === currentSeasonId)) {
          if (!activeSeason) {
            setActiveSeason(currentSeasonId);
          }
        } else if (!activeSeason) {
          // Sort seasons by start_date to get the latest season
          const sortedSeasons = [...state.seasons].sort(
            (a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0)
          );
          setActiveSeason(sortedSeasons[0].id);
        }

        setLoading(false);
      }
    });

    // Initialize with current store state — fetch if not loaded yet
    const currentState = useStore.getState();
    if (currentState.seasons && currentState.seasons.length > 0) {
      setSeasons(currentState.seasons);
      setError(null);

      const currentSeasonId = currentState.currentSeason?.id;
      if (currentSeasonId && currentState.seasons.find((s) => s.id === currentSeasonId)) {
        setActiveSeason(currentSeasonId);
      } else {
        const sortedSeasons = [...currentState.seasons].sort(
          (a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0)
        );
        setActiveSeason(sortedSeasons[0].id);
      }

      setLoading(false);
    } else {
      // Store empty — trigger fetch (subscriber above will handle the response)
      currentState.fetchSeasons();
    }

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load fixtures and group stages when season changes
  useEffect(() => {
    if (!activeSeason) {
      return;
    }

    // Reset view to group-stage when season changes
    setActiveView('group-stage');

    const loadFixturesData = async () => {
      setFixturesLoading(true);
      try {
        // Fetch fixtures, group stages, and all matches with player stats
        await Promise.all([
          useStore.getState().fetchFixtures(activeSeason),
          useStore.getState().fetchGroupStages(activeSeason),
          useStore.getState().fetchAllMatches(activeSeason),
        ]);

        // Update local state with the fetched data from store
        const state = useStore.getState();
        if (state.fixtures && state.fixtures[activeSeason]) {
          setFixturesData(state.fixtures[activeSeason]);
        }
        if (state.groupStages && state.groupStages[activeSeason]) {
          setGroupStages(state.groupStages[activeSeason]);
        }
        if (state.allMatches && state.allMatches[activeSeason]) {
          setAllMatchesData(state.allMatches[activeSeason]);
        }
      } catch (error) {
        console.error('Error loading fixtures data:', error);
        setFixturesData(null);
        setGroupStages(null);
      } finally {
        setFixturesLoading(false);
      }
    };

    loadFixturesData();
  }, [activeSeason]);

  const getMatchStatusClass = (status) => {
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'scheduled':
        return 'status-scheduled';
      case 'upcoming':
        return 'status-upcoming';
      default:
        return 'status-upcoming';
    }
  };

  const getMatchStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'COMPLETED';
      case 'scheduled':
        return '📅 SCHEDULED';
      default:
        return '⏳ UPCOMING';
    }
  };

  const openMatchDetails = (match) => {
    if (match.status === 'completed') {
      setSelectedMatch(match);
      setShowMatchModal(true);
      fetchPlayerStats(match);
    }
  };

  const fetchPlayerStats = (match) => {
    setLoadingPlayerStats(true);
    try {
      // Find the match in allMatchesData by match_id
      if (allMatchesData && allMatchesData.matches) {
        const matchData = allMatchesData.matches.find((m) => m.match_id === match.match_id);

        if (matchData) {
          // Transform the data to match the expected format
          const playerStatsData = {
            match_id: matchData.match_id,
            season_id: matchData.season_id || activeSeason,
            attacker_team: matchData.attacker_team,
            defender_team: matchData.defender_team,
          };
          setMatchPlayerStats(playerStatsData);
        } else {
          console.warn('Match data not found in store for match:', match.match_id);
          setMatchPlayerStats(null);
        }
      } else {
        console.warn('All matches data not loaded yet');
        setMatchPlayerStats(null);
      }
    } catch (error) {
      console.error('Error fetching player stats from store:', error);
      setMatchPlayerStats(null);
    } finally {
      setLoadingPlayerStats(false);
    }
  };

  const calculateGroupStandings = (group, groupMatches) => {
    // Initialize standings for all teams
    const standings = group.teams.map((team) => ({
      team_id: team.team_id,
      team_name: team.team_name,
      logo: team.logo,
      matches_played: 0,
      wins: 0,
      losses: 0,
      rounds_won: 0,
      rounds_lost: 0,
      round_diff: 0,
      points: 0,
    }));

    // Calculate stats from completed matches
    groupMatches.forEach((match) => {
      if (match.status !== 'completed') return;

      const attackerStats = standings.find((s) => s.team_id === match.attacker_team?.id);
      const defenderStats = standings.find((s) => s.team_id === match.defender_team?.id);

      if (attackerStats && defenderStats) {
        const attackerScore = match.attacker_team?.score || 0;
        const defenderScore = match.defender_team?.score || 0;

        attackerStats.matches_played++;
        defenderStats.matches_played++;

        let roundsForStatsAttacker = attackerScore;
        let roundsForStatsDefender = defenderScore;

        if (attackerScore >= 12 && defenderScore >= 12) {
          roundsForStatsAttacker = 0;
          roundsForStatsDefender = 0;
        }

        attackerStats.rounds_won += roundsForStatsAttacker;
        attackerStats.rounds_lost += roundsForStatsDefender;
        defenderStats.rounds_won += roundsForStatsDefender;
        defenderStats.rounds_lost += roundsForStatsAttacker;

        if (attackerScore > defenderScore) {
          attackerStats.wins++;
          attackerStats.points += 3;
          defenderStats.losses++;
        } else if (defenderScore > attackerScore) {
          defenderStats.wins++;
          defenderStats.points += 3;
          attackerStats.losses++;
        }
      }
    });

    standings.forEach((s) => {
      s.round_diff = s.rounds_won - s.rounds_lost;
    });

    // ── Q/E logic ─────────────────────────────────────────────
    const PTS_PER_WIN   = 3;
    const DIRECT_SPOTS  = 2;
    const PLAYOFF_SPOTS = 4;

    // Count remaining from actual schedule
    standings.forEach((s) => {
      s.remaining = groupMatches.filter(
        (m) => m.status !== 'completed' &&
          (m.attacker_team?.id === s.team_id || m.defender_team?.id === s.team_id)
      ).length;
      s.max_points = s.points + s.remaining * PTS_PER_WIN;
    });

    const sorted = [...standings].sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.round_diff !== a.round_diff) return b.round_diff - a.round_diff;
      return b.rounds_won - a.rounds_won;
    });

    // Check if all group matches are done
    const allMatchesDone = groupMatches.every((m) => m.status === 'completed');

    sorted.forEach((team, idx) => {
      if (allMatchesDone) {
        // All done — use final position
        if (idx < DIRECT_SPOTS)        team.status = 'Q';
        else if (idx < PLAYOFF_SPOTS)  team.status = 'q';
        else                           team.status = 'E';
        return;
      }

      // Still ongoing — conservative math
      const fourthPts = sorted[PLAYOFF_SPOTS - 1]?.points ?? 0;

      // E: even winning all remaining, can't reach 4th
      if (team.max_points < fourthPts) {
        team.status = 'E';
        return;
      }

      // Count teams that can strictly surpass on points
      const canPassMe = sorted.filter(
        (t) => t.team_id !== team.team_id && t.max_points > team.points
      ).length;

      if (canPassMe < DIRECT_SPOTS)        team.status = 'Q';
      else if (canPassMe < PLAYOFF_SPOTS)  team.status = 'q';
      else                                 team.status = null;
    });

    return sorted;
  };

  const renderTimeline = () => {
    if (!fixturesData || !fixturesData.grouped_fixtures) {
      return (
        <div className="no-fixtures-message">
          <h3>No matches to display</h3>
          <p>Timeline will appear here once matches are scheduled.</p>
        </div>
      );
    }

    // Collect all matches from all groups
    const allMatches = [];
    fixturesData.grouped_fixtures.forEach((groupFixture) => {
      const groupInfo = groupStages?.groups?.find((g) => g.group_id === groupFixture.group_id);
      const groupName = groupInfo?.group_name || 'Unknown Group';

      groupFixture.matches?.forEach((match) => {
        allMatches.push({
          ...match,
          group_name: groupName,
          group_id: groupFixture.group_id,
        });
      });
    });

    // Filter matches by group and team
    let filteredMatches = allMatches;
    if (timelineGroupFilter !== 'all') {
      filteredMatches = allMatches.filter((m) => m.group_id === timelineGroupFilter);
    }
    if (timelineTeamFilter !== 'all') {
      filteredMatches = filteredMatches.filter(
        (m) => m.attacker_team?.id === timelineTeamFilter || m.defender_team?.id === timelineTeamFilter
      );
    }

    // Sort by date
    filteredMatches.sort((a, b) => {
      if (!a.match_datetime && !b.match_datetime) return 0;
      if (!a.match_datetime) return 1;
      if (!b.match_datetime) return -1;
      return new Date(a.match_datetime) - new Date(b.match_datetime);
    });

    // Get regular groups
    const regularGroups =
      groupStages?.groups
        ?.filter((group) => group.stages === true)
        .sort((a, b) => (a.group_name || '').localeCompare(b.group_name || '')) || [];

    // Get unique teams from filtered matches (after group filter)
    let matchesForTeamFilter = allMatches;
    if (timelineGroupFilter !== 'all') {
      matchesForTeamFilter = allMatches.filter((m) => m.group_id === timelineGroupFilter);
    }
    const uniqueTeamsForTimeline = getUniqueTeams(
      matchesForTeamFilter.map((m) => ({ attacker_team: m.attacker_team, defender_team: m.defender_team }))
    );

    return (
      <div className="timeline-container">
        <div className="timeline-header">
          <h2>Match Timeline</h2>
          <div className="timeline-filters">
            <div className="timeline-filter">
              <label htmlFor="timeline-group-filter">Filter by Group:</label>
              <select
                id="timeline-group-filter"
                value={timelineGroupFilter}
                onChange={(e) => {
                  setTimelineGroupFilter(e.target.value);
                  setTimelineTeamFilter('all');
                }}
                className="filter-select"
              >
                <option value="all">All Groups</option>
                {regularGroups.map((group) => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="timeline-filter">
              <label htmlFor="timeline-team-filter">Filter by Team:</label>
              <select
                id="timeline-team-filter"
                value={timelineTeamFilter}
                onChange={(e) => setTimelineTeamFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Teams</option>
                {uniqueTeamsForTimeline.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {filteredMatches.length > 0 ? (
          <div className="timeline-wrapper">
            <div className="timeline-vertical">
            {filteredMatches.map((match, index) => {

              const isIndividualTeamSelected =
           timelineTeamFilter && timelineTeamFilter !== 'all';

              const selectedTeamPlayed =String(match.attacker_team?.id) === String(timelineTeamFilter) ||String(match.defender_team?.id) === String(timelineTeamFilter);

                 const winnerTeamId =match.winner?.id ||match.winner?.team?.id ||match.winner_team?.id;

                const selectedTeamLost =isIndividualTeamSelected &&selectedTeamPlayed &&winnerTeamId &&String(winnerTeamId) !== String(timelineTeamFilter);
const selectedTeamIsDefender =
  String(match.defender_team?.id) === String(timelineTeamFilter);

const leftTeam =
  isIndividualTeamSelected && selectedTeamIsDefender
    ? match.defender_team
    : match.attacker_team;

const rightTeam =
  isIndividualTeamSelected && selectedTeamIsDefender
    ? match.attacker_team
    : match.defender_team;

const leftScore =
  isIndividualTeamSelected && selectedTeamIsDefender
    ? match.defender_team?.score
    : match.attacker_team?.score;

const rightScore =
  isIndividualTeamSelected && selectedTeamIsDefender
    ? match.attacker_team?.score
    : match.defender_team?.score;

              return (
                <div key={match.match_id} className={`timeline-item ${getMatchStatusClass(match.status)}`}>
                  <div className="timeline-marker">
                    <span className="timeline-dot"></span>
                  </div>

                <div className={`timeline-content ${selectedTeamLost ? 'timeline-content-lost' : ''}`}>
                  <div className="timeline-group-badge">{match.group_name}</div>

                  <div className="timeline-match">
                    <div className="timeline-team">
                      {leftTeam?.logo ? (
                        <img
                          src={leftTeam.logo}
                          alt={leftTeam.name}
                          className="timeline-team-logo"
                        />
                      ) : (
                        <div className="timeline-team-logo-placeholder">
                          {leftTeam?.name?.charAt(0) || 'A'}
                        </div>
                      )}
                      <span className="timeline-team-name">{leftTeam?.name || 'TBD'}</span>
                    </div>

                    <div className="timeline-score">
                      <span className="score-value">{leftScore ?? '-'}</span>
                      <span className="score-separator">-</span>
                      <span className="score-value">{rightScore ?? '-'}</span>
                    </div>

                    <div className="timeline-team">
                      <span className="timeline-team-name">{rightTeam?.name || 'TBD'}</span>
                      {rightTeam?.logo ? (
                        <img
                          src={rightTeam.logo}
                          alt={rightTeam.name}
                          className="timeline-team-logo"
                        />
                      ) : (
                        <div className="timeline-team-logo-placeholder">
                          {rightTeam?.name?.charAt(0) || 'D'}
                        </div>
                      )}
                    </div>
                  </div>
                  

                  <div className="timeline-details">
                    {match.match_datetime && (
                      <span className="timeline-datetime">
                        📅{' '}
                        {new Date(match.match_datetime).toLocaleString('en-US', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </span>
                    )}
                    <span className={`timeline-status ${getMatchStatusClass(match.status)}`}>
                      {getMatchStatusText(match.status)}
                    </span>
                    {match.winner && match.status === 'completed' && (
                      <span className="timeline-winner">🏆 {match.winner.name}</span>
                    )}
                    {match.youtube_location && (
                      <a
                        href={match.youtube_location}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="timeline-youtube-link"
                        title="Watch on YouTube"
                      >
                        ▶️ Watch
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )})}
            </div>
          </div>
        ) : (
          <p className="no-matches">No matches in timeline</p>
        )}
      </div>
    );
  };

  const renderGroupStage = () => {
    if (!groupStages || !groupStages.groups || groupStages.groups.length === 0) {
      return (
        <div className="no-fixtures-message">
          <h3>No groups configured for this season</h3>
          <p>Groups and matches will appear here once they are set up.</p>
        </div>
      );
    }

    // Filter only groups where stages = true (regular group stages)
    const regularGroups = groupStages.groups
      .filter((group) => group.stages === true)
      .sort((a, b) => (a.group_name || '').localeCompare(b.group_name || ''));

    if (regularGroups.length === 0) {
      return (
        <div className="no-fixtures-message">
          <h3>No group stages configured for this season</h3>
          <p>Group stage matches will appear here once they are set up.</p>
        </div>
      );
    }

    // Collect all matches from all groups
    const allMatches = [];
    regularGroups.forEach((group) => {
      const groupMatches = fixturesData?.grouped_fixtures?.find((gf) => gf.group_id === group.group_id)?.matches || [];
      groupMatches.forEach((match) => {
        allMatches.push({
          ...match,
          group_name: group.group_name,
          group_id: group.group_id,
        });
      });
    });

    // Sort matches by match_datetime (earliest first, nulls last)
    allMatches.sort((a, b) => {
      if (!a.match_datetime && !b.match_datetime) return 0;
      if (!a.match_datetime) return 1;
      if (!b.match_datetime) return -1;
      return new Date(a.match_datetime) - new Date(b.match_datetime);
    });

    // Apply filters to matches
    const filteredMatches = allMatches.filter((match) => {
      // Filter by group
      if (selectedGroupFilter !== 'all' && match.group_id !== selectedGroupFilter) {
        return false;
      }

      // Filter by team
      if (selectedTeamFilter !== 'all') {
        const teamMatches =
          match.attacker_team?.id === selectedTeamFilter || match.defender_team?.id === selectedTeamFilter;
        if (!teamMatches) {
          return false;
        }
      }

      // Filter by status
      if (selectedStatusFilter !== 'all') {
        if (selectedStatusFilter === 'scheduled' && match.status !== 'scheduled') {
          return false;
        }
        if (selectedStatusFilter === 'completed' && match.status !== 'completed') {
          return false;
        }
      }

      return true;
    });

    // Calculate match stats for each group
    const groupMatchStats = regularGroups.map((group) => {
      const groupMatches = fixturesData?.grouped_fixtures?.find((gf) => gf.group_id === group.group_id)?.matches || [];
      const completedMatches = groupMatches.filter((m) => m.status === 'completed').length;
      const scheduledMatches = groupMatches.filter((m) => m.status === 'scheduled').length;
      const totalMatches = groupMatches.length;
      const remainingMatches = totalMatches - completedMatches;

      return {
        group_name: group.group_name,
        group_id: group.group_id,
        completed: completedMatches,
        scheduled: scheduledMatches,
        remaining: remainingMatches,
        total: totalMatches,
      };
    });

    // Calculate overall stats
    const overallStats = {
      completed: groupMatchStats.reduce((sum, g) => sum + g.completed, 0),
      scheduled: groupMatchStats.reduce((sum, g) => sum + g.scheduled, 0),
      total: groupMatchStats.reduce((sum, g) => sum + g.total, 0),
    };
    overallStats.remaining = overallStats.total - overallStats.completed;

    return (
      <div className="group-stage-container">
        <div
          className="table-legend"
          style={{
            marginBottom: '20px',
            fontSize: '0.9em',
            color: '#fff',
            textAlign: 'center',
            padding: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
          }}
        >
          MP: Matches Played • W: Wins • L: Losses • RW: Rounds Won • RL: Rounds Lost • RD: Round Difference • PTS:
          Points
        </div>

        <div
          className="format-note"
          style={{
            marginBottom: '20px',
            fontSize: '0.85em',
            color: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center',
            padding: '8px 14px',
            backgroundColor: 'rgba(60, 226, 194, 0.05)',
            border: '1px solid rgba(60, 226, 194, 0.15)',
            borderRadius: '8px',
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: 'rgba(60, 226, 194, 0.9)' }}>FORMAT:</strong> Each team plays{' '}
          <strong style={{ color: '#fff' }}>6 out of 7</strong> possible opponents in their group (partial
          round-robin). We didn't want the tournament to be stretched out and drag on like last season, so the
          group stage was kept tighter — not every team will face every other team.
        </div>

        {/* Small Group Cards Grid */}
        <div className="groups-cards-grid">
          {regularGroups.map((group) => {
            // Find matches for this group from fixturesData
            const groupMatches =
              fixturesData?.grouped_fixtures?.find((gf) => gf.group_id === group.group_id)?.matches || [];

            // Calculate standings
            const standings = calculateGroupStandings(group, groupMatches);

            return (
              <div key={group.group_id} className="group-card-compact">
                <div className="group-card-header">
                  <h3>{group.group_name}</h3>
                  {group.match_format && <span className="group-format-badge">BO{group.match_format}</span>}
                </div>

                <div className="group-card-standings">
                  <table className="standings-table-compact">
                    <thead>
                      <tr>
                        <th className="team-col-compact">Team</th>
                        <th className="stat-col-compact">MP</th>
                        <th className="stat-col-compact">W</th>
                        <th className="stat-col-compact">L</th>
                        <th className="stat-col-compact">RW</th>
                        <th className="stat-col-compact">RL</th>
                        <th className="stat-col-compact">RD</th>
                        <th className="stat-col-compact points-col-compact">PTS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((team, index) => (
                        <tr
                          key={team.team_id}
                          className={`standings-row-compact ${
                            index === 0 ? 'rank-1' :
                            index === 1 ? 'rank-2 playoff-line-2' :
                            index === 2 ? 'rank-3' :
                            index === 3 ? 'rank-3 playoff-line-4' : ''
                          }`}
                        >
                          <td className="team-col-compact">
                            <div className="team-cell-compact">
                              {team.logo ? (
                                <img src={team.logo} alt={team.team_name} className="team-logo-compact" />
                              ) : (
                                <div className="team-logo-compact-placeholder">
                                  {team.team_name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <span className="team-name-compact">{team.team_name}</span>
                              {team.status === 'Q' && <span className="qual-badge qual-direct" title="Qualified — Direct Playoff">Q</span>}
                              {team.status === 'q' && <span className="qual-badge qual-path" title="Qualified — Playoff Qualifier">Q</span>}
                              {team.status === 'E' && <span className="qual-badge qual-elim" title="Eliminated">E</span>}
                            </div>
                          </td>
                          <td className="stat-col-compact">{team.matches_played}</td>
                          <td className="stat-col-compact">{team.wins}</td>
                          <td className="stat-col-compact">{team.losses}</td>
                          <td className="stat-col-compact">{team.rounds_won}</td>
                          <td className="stat-col-compact">{team.rounds_lost}</td>
                          <td className="stat-col-compact">
                            <span className={team.round_diff >= 0 ? 'positive-diff' : 'negative-diff'}>
                              {team.round_diff >= 0 ? '+' : ''}
                              {team.round_diff}
                            </span>
                          </td>
                          <td className="stat-col-compact points-col-compact">
                            <span className="points-value-compact">{team.points}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>

        {/* Match Statistics Summary */}
        <div className="matches-stats-summary">
          <div className="stats-overview">
            <h3 className="stats-title">Match Statistics</h3>
            <div className="stats-cards">
              <div className="stat-card overall-stat">
                <div className="stat-icon">⚔️</div>
                <div className="stat-label">Total Matches</div>
                <div className="stat-value">{overallStats.total}</div>
              </div>
              <div className="stat-card completed-stat">
                <div className="stat-icon">✓</div>
                <div className="stat-label">Completed</div>
                <div className="stat-value">{overallStats.completed}</div>
              </div>
              <div className="stat-card remaining-stat">
                <div className="stat-icon">⏱</div>
                <div className="stat-label">Remaining</div>
                <div className="stat-value">{overallStats.remaining}</div>
              </div>
              {overallStats.total > 0 && (
                <div className="stat-card progress-stat">
                  <div className="stat-icon">⭐</div>
                  <div className="stat-label">Progress</div>
                  <div className="stat-value">{Math.round((overallStats.completed / overallStats.total) * 100)}%</div>
                </div>
              )}
            </div>
          </div>

          <div className="group-stats">
            <h4 className="group-stats-title">Match Status by Group</h4>
            <div className="group-stats-grid">
              {groupMatchStats.map((groupStat) => (
                <div key={groupStat.group_id} className="group-stat-card">
                  <div className="group-stat-name">{groupStat.group_name}</div>
                  <div className="group-stat-row">
                    <div className="group-stat-item">
                      <span className="stat-label-small">Completed</span>
                      <span className="stat-value-small completed">
                        <span className="icon-badge">✓</span> {groupStat.completed}
                      </span>
                    </div>
                    <div className="stat-divider-vertical"></div>
                    <div className="group-stat-item">
                      <span className="stat-label-small">Remaining</span>
                      <span className="stat-value-small remaining">
                        <span className="icon-badge">⏱</span> {groupStat.remaining}
                      </span>
                    </div>
                    <div className="stat-divider-vertical"></div>
                    <div className="group-stat-item">
                      <span className="stat-label-small">Total</span>
                      <span className="stat-value-small total">
                        <span className="icon-badge">⚔️</span> {groupStat.total}
                      </span>
                    </div>
                  </div>
                  <div className="group-stat-progress">
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${groupStat.total > 0 ? (groupStat.completed / groupStat.total) * 100 : 0}%` }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {groupStat.total > 0 ? Math.round((groupStat.completed / groupStat.total) * 100) : 0}% Complete
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* All Matches Section */}
        <div className="all-matches-section">
          <h2 className="all-matches-title">All Group Stage Matches</h2>

          {/* Filters */}
          <div className="matches-filters">
            <div className="filter-group">
              <label htmlFor="group-filter">Filter by Group:</label>
              <select
                id="group-filter"
                value={selectedGroupFilter}
                onChange={(e) => setSelectedGroupFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Groups</option>
                {regularGroups.map((group) => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="status-filter">Filter by Status:</label>
              <select
                id="status-filter"
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Matches</option>
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="team-filter">Filter by Team:</label>
              <select
                id="team-filter"
                value={selectedTeamFilter}
                onChange={(e) => setSelectedTeamFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Teams</option>
                {getUniqueTeams(allMatches).map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {filteredMatches.length > 0 ? (
            <div className="all-matches-grid">
              {filteredMatches.map((match) => (
                <div
                  key={match.match_id}
                  className={`match-card-common ${getMatchStatusClass(match.status)} ${
                    match.status === 'completed' ? 'match-clickable' : ''
                  }`}
                  onClick={() => openMatchDetails(match)}
                  role={match.status === 'completed' ? 'button' : 'presentation'}
                  tabIndex={match.status === 'completed' ? 0 : -1}
                >
                  <div className="match-card-group-label-center">{match.group_name}</div>
                  <div className="match-teams-row">
                    <div className="team-with-logo">
                      {match.attacker_team?.logo ? (
                        <img
                          src={match.attacker_team.logo}
                          alt={match.attacker_team.name}
                          className="team-logo-small"
                        />
                      ) : (
                        <div className="team-logo-small-placeholder">{match.attacker_team?.name?.charAt(0) || 'A'}</div>
                      )}
                      <span className="team-name-small">{match.attacker_team?.name || 'TBD'}</span>
                    </div>
                    <div className="match-score-vs">
                      <span className="team-score-inline">{match.attacker_team?.score ?? '-'}</span>
                      <span className="match-vs-center">VS</span>
                      <span className="team-score-inline">{match.defender_team?.score ?? '-'}</span>
                    </div>
                    <div className="team-with-logo">
                      <span className="team-name-small">{match.defender_team?.name || 'TBD'}</span>
                      {match.defender_team?.logo ? (
                        <img
                          src={match.defender_team.logo}
                          alt={match.defender_team.name}
                          className="team-logo-small"
                        />
                      ) : (
                        <div className="team-logo-small-placeholder">{match.defender_team?.name?.charAt(0) || 'D'}</div>
                      )}
                    </div>
                  </div>
                  <div className="match-info">
                    <div className="match-info-row">
                      {match.match_datetime && (
                        <span className="match-datetime">
                          {new Date(match.match_datetime).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                          })}
                        </span>
                      )}
                      {match.venue &&
                        (match.youtube_location ? (
                          <a
                            href={match.youtube_location}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="match-venue-link"
                          >
                            📍 {match.venue}
                          </a>
                        ) : (
                          <span className="match-venue">📍 {match.venue}</span>
                        ))}
                      {match.venue_location && <span className="match-venue-location">{match.venue_location}</span>}
                    </div>
                    <div className="match-info-row">
                      <span className="match-winner">🏆 Winner: {match.winner ? match.winner.name : 'Awaited'}</span>
                      <span className={`match-status ${getMatchStatusClass(match.status)}`}>
                        {getMatchStatusText(match.status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-matches">No matches scheduled yet</p>
          )}
        </div>
      </div>
    );
  };

  const renderPlayoffs = () => {
    if (!fixturesData || !fixturesData.grouped_fixtures) {
      return (
        <div className="no-fixtures-message">
          <h3>No playoff matches scheduled</h3>
          <p>Playoff bracket will appear here once matches are scheduled.</p>
        </div>
      );
    }

    // Filter playoff groups by name patterns (more reliable than stages flag)
    const playoffGroups = fixturesData.grouped_fixtures.filter((group) => {
      const name = group.group_name.toLowerCase();
      return (
        name.includes('qualifier') ||
        name.includes('playoff') ||
        name.includes('semi') ||
        name.includes('eliminator') ||
        name.includes('elimination') ||
        name.includes('final')
      );
    });

    if (playoffGroups.length === 0) {
      return (
        <div className="no-fixtures-message">
          <h3>No playoff matches scheduled</h3>
          <p>Playoff bracket will appear here once matches are scheduled.</p>
        </div>
      );
    }

    // Sort playoff groups by name (Qualifier 1-4, Playoffs 1-4, Semifinals, Eliminator, Lower Finals, Grand Finals)
    const sortedPlayoffGroups = [...playoffGroups].sort((a, b) => {
      const order = {
        qualifier: 1,
        playoff: 2,
        semifinal: 3,
        eliminator: 4,
        lowerfinal: 5,
        final: 6,
      };

      const aName = a.group_name.toLowerCase();
      const bName = b.group_name.toLowerCase();

      const aType = aName.includes('qualifier')
        ? 'qualifier'
        : aName.includes('semi')
        ? 'semifinal'
        : aName.includes('eliminator') || aName.includes('elimination')
        ? 'eliminator'
        : aName.includes('lower') && aName.includes('final')
        ? 'lowerfinal'
        : aName.includes('final')
        ? 'final'
        : 'playoff';

      const bType = bName.includes('qualifier')
        ? 'qualifier'
        : bName.includes('semi')
        ? 'semifinal'
        : bName.includes('eliminator') || bName.includes('elimination')
        ? 'eliminator'
        : bName.includes('lower') && bName.includes('final')
        ? 'lowerfinal'
        : bName.includes('final')
        ? 'final'
        : 'playoff';

      if (order[aType] !== order[bType]) {
        return order[aType] - order[bType];
      }

      return a.group_name.localeCompare(b.group_name);
    });

    // Group stages by type for tree structure
    const stagesByType = {
      qualifiers: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        return name.includes('qualifier') && !name.includes('lower');
      }),
      lowerQualifiers: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        return name.includes('lower') && name.includes('qualifier');
      }),
      playoffs: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        return (
          name.includes('playoff') &&
          !name.includes('semi') &&
          !name.includes('final') &&
          !name.includes('eliminator') &&
          !name.includes('elimination')
        );
      }),
      upperSemifinals: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        return (name.includes('semi') || name.includes('semi-final')) && !name.includes('lower');
      }),
      lowerSemifinals: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        return name.includes('lower') && (name.includes('semi') || name.includes('semi-final'));
      }),
      eliminator: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        return (name.includes('eliminator') || name.includes('elimination')) && !name.includes('final');
      }),
      lowerFinals: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        const isLowerFinal = name.includes('lower') && name.includes('final') && !name.includes('semi');
        return isLowerFinal;
      }),
      finals: sortedPlayoffGroups.filter((g) => {
        const name = g.group_name.toLowerCase();
        return (
          name.includes('final') && !name.includes('semi') && !name.includes('eliminator') && !name.includes('lower')
        );
      }),
    };

    // Function to get all matches for a bracket (team pair)
    const getBracketMatches = (playoffGroup, team1Id, team2Id) => {
      const groupMatches =
        fixturesData?.grouped_fixtures?.find((gf) => gf.group_id === playoffGroup.group_id)?.matches || [];

      return groupMatches.filter(
        (m) =>
          (m.attacker_team?.id === team1Id && m.defender_team?.id === team2Id) ||
          (m.attacker_team?.id === team2Id && m.defender_team?.id === team1Id)
      );
    };

    // Function to calculate series score
    const getSeriesScore = (matches, team1Id, team2Id) => {
      let team1Wins = 0;
      let team2Wins = 0;

      matches.forEach((match) => {
        if (match.winner?.id === team1Id) team1Wins++;
        if (match.winner?.id === team2Id) team2Wins++;
      });

      return { team1Wins, team2Wins };
    };

    // Function to open bracket modal
    const openBracketModal = (playoffGroup, team1, team2) => {
      const matches = getBracketMatches(playoffGroup, team1?.id, team2?.id);
      setSelectedBracket({
        group: playoffGroup,
        team1,
        team2,
        matches,
      });
      setShowBracketModal(true);
    };

    // Render a single bracket round column
    const renderBracketRound = (playoffGroup, title) => {
      const groupMatches =
        fixturesData?.grouped_fixtures?.find((gf) => gf.group_id === playoffGroup.group_id)?.matches || [];

      // Group matches by team pairs
      const bracketPairs = [];
      const processedPairs = new Set();

      groupMatches.forEach((match) => {
        const team1Id = match.attacker_team?.id;
        const team2Id = match.defender_team?.id;

        // Create a unique key for this match/bracket
        const pairKey = team1Id && team2Id ? [team1Id, team2Id].sort().join('-') : `match-${match.match_id}`;

        if (!processedPairs.has(pairKey)) {
          processedPairs.add(pairKey);

          // If teams are assigned, get all matches for this pair
          let allPairMatches = [];
          let seriesScore = { team1Wins: 0, team2Wins: 0 };
          let seriesWinner = null;

          if (team1Id && team2Id) {
            allPairMatches = getBracketMatches(playoffGroup, team1Id, team2Id);
            seriesScore = getSeriesScore(allPairMatches, team1Id, team2Id);

            // Determine series winner based on match_format (Best of X)
            const matchFormat = playoffGroup.match_format || 1;
            const winsNeeded = Math.ceil(matchFormat / 2); // e.g., BO5 needs 3 wins, BO3 needs 2 wins

            if (seriesScore.team1Wins >= winsNeeded) {
              seriesWinner = match.attacker_team;
            } else if (seriesScore.team2Wins >= winsNeeded) {
              seriesWinner = match.defender_team;
            }
          } else {
            // For TBD matches, just use the single match
            allPairMatches = [match];
          }

          bracketPairs.push({
            team1: match.attacker_team || { id: null, name: null, logo: null },
            team2: match.defender_team || { id: null, name: null, logo: null },
            matches: allPairMatches,
            seriesScore,
            winner: seriesWinner,
            isTBD: !team1Id || !team2Id,
          });
        }
      });

      if (bracketPairs.length === 0) {
        // Show empty bracket placeholder
        return (
          <div className="bracket-round">
            <div className="round-matches">
              <div className="bracket-match status-upcoming">
                <div className="bracket-match-inner">
                  <div className="bracket-team top">
                    <div className="team-info">
                      <span className="team-name">TBD</span>
                    </div>
                    <span className="team-score">-</span>
                  </div>
                  <div className="bracket-divider"></div>
                  <div className="bracket-team bottom">
                    <div className="team-info">
                      <span className="team-name">TBD</span>
                    </div>
                    <span className="team-score">-</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className="bracket-round">
          <div className="round-matches">
            {bracketPairs.map((bracket, idx) => (
              <div
                key={`${bracket.team1?.id || 'tbd1'}-${bracket.team2?.id || 'tbd2'}-${idx}`}
                className={`bracket-match ${
                  bracket.winner ? 'status-completed' : bracket.isTBD ? 'status-tbd' : 'status-upcoming'
                }`}
              >
                <div className="bracket-match-inner">
                  <div className={`bracket-team top ${bracket.winner?.id === bracket.team1?.id ? 'winner' : ''}`}>
                    <div className="team-info">
                      {bracket.team1?.logo && (
                        <img src={bracket.team1.logo} alt={bracket.team1.name} className="team-logo-bracket" />
                      )}
                      <span className={`team-name ${!bracket.team1?.name ? 'tbd-text' : ''}`}>
                        {bracket.team1?.name || 'TBD'}
                      </span>
                    </div>
                    <span className={`team-score ${bracket.winner?.id === bracket.team1?.id ? 'winner' : ''}`}>
                      {bracket.winner?.id === bracket.team1?.id && ' 👑'}
                      {bracket.isTBD ? '-' : bracket.seriesScore.team1Wins}
                    </span>
                  </div>
                  <div className="bracket-divider"></div>
                  <div className={`bracket-team bottom ${bracket.winner?.id === bracket.team2?.id ? 'winner' : ''}`}>
                    <div className="team-info">
                      {bracket.team2?.logo && (
                        <img src={bracket.team2.logo} alt={bracket.team2.name} className="team-logo-bracket" />
                      )}
                      <span className={`team-name ${!bracket.team2?.name ? 'tbd-text' : ''}`}>
                        {bracket.team2?.name || 'TBD'}
                      </span>
                    </div>
                    <span className={`team-score ${bracket.winner?.id === bracket.team2?.id ? 'winner' : ''}`}>
                      {bracket.winner?.id === bracket.team2?.id && ' 👑'}
                      {bracket.isTBD ? '-' : bracket.seriesScore.team2Wins}
                    </span>
                  </div>
                  {!bracket.isTBD && (
                    <button
                      className="bracket-info-icon-btn"
                      onClick={() => openBracketModal(playoffGroup, bracket.team1, bracket.team2)}
                      title="View match details"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="16" x2="12" y2="12"></line>
                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    // Check if there are any lower bracket stages
    const hasLowerBracket =
      stagesByType.lowerQualifiers.length > 0 ||
      stagesByType.eliminator.length > 0 ||
      stagesByType.lowerSemifinals.length > 0 ||
      stagesByType.lowerFinals.length > 0;

    return (
      <>
        <div className="tournament-bracket-container">
          {hasLowerBracket ? (
            <>
              {/* Top Row: Upper Bracket */}
              <div className="bracket-row bracket-row-upper">
                <div className="bracket-tree">
                  {/* Column 1: Qualifiers (top) */}
                  <div className="bracket-stage-column">
                    {stagesByType.qualifiers.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>Qualifiers</h3>
                          <span className="stage-format">Best of {stagesByType.qualifiers[0]?.match_format || 3}</span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.qualifiers.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Column 2: Playoffs (top) */}
                  <div className="bracket-stage-column">
                    {stagesByType.playoffs.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>Playoffs</h3>
                          <span className="stage-format">Best of {stagesByType.playoffs[0]?.match_format || 3}</span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.playoffs.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Column 3: Upper Semifinals */}
                  <div className="bracket-stage-column">
                    {stagesByType.upperSemifinals.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>Semifinals</h3>
                          <span className="stage-format">
                            Best of {stagesByType.upperSemifinals[0]?.match_format || 3}
                          </span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.upperSemifinals.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Column 4: Empty space for alignment */}
                  <div className="bracket-stage-column bracket-placeholder"></div>

                  {/* Column 5: Grand Finals */}
                  <div className="bracket-stage-column finals-column">
                    {stagesByType.finals.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>🏆 Grand Finals</h3>
                          <span className="stage-format">Best of {stagesByType.finals[0]?.match_format || 5}</span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.finals.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Bottom Row: Lower Bracket */}
              <div className="bracket-row bracket-row-lower">
                <div className="bracket-tree">
                  {/* Column 1: Lower Qualifiers */}
                  <div className="bracket-stage-column">
                    {stagesByType.lowerQualifiers.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>Lower Qualifiers</h3>
                          <span className="stage-format">
                            Best of {stagesByType.lowerQualifiers[0]?.match_format || 3}
                          </span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.lowerQualifiers.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Column 2: Eliminators */}
                  <div className="bracket-stage-column">
                    {stagesByType.eliminator.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>Eliminators</h3>
                          <span className="stage-format">Best of {stagesByType.eliminator[0]?.match_format || 3}</span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.eliminator.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Column 3: Lower Semifinals */}
                  <div className="bracket-stage-column">
                    {stagesByType.lowerSemifinals.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>Lower Semifinals</h3>
                          <span className="stage-format">
                            Best of {stagesByType.lowerSemifinals[0]?.match_format || 3}
                          </span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.lowerSemifinals.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Column 4: Lower Finals */}
                  <div className="bracket-stage-column">
                    {stagesByType.lowerFinals.length > 0 && (
                      <>
                        <div className="stage-column-header">
                          <h3>Lower Finals</h3>
                          <span className="stage-format">Best of {stagesByType.lowerFinals[0]?.match_format || 3}</span>
                        </div>
                        <div className="stage-rounds">
                          {stagesByType.lowerFinals.map((group) => (
                            <div key={group.group_id}>{renderBracketRound(group)}</div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Column 5: Empty placeholder for alignment with Grand Finals */}
                  <div className="bracket-stage-column bracket-placeholder"></div>
                </div>
              </div>
            </>
          ) : (
            /* Single Row: Only Upper Bracket */
            <div className="bracket-tree">
              {/* Qualifiers */}
              {stagesByType.qualifiers.length > 0 && (
                <div className="bracket-stage-column">
                  <div className="stage-column-header">
                    <h3>Qualifiers</h3>
                    <span className="stage-format">Best of {stagesByType.qualifiers[0]?.match_format || 3}</span>
                  </div>
                  <div className="stage-rounds">
                    {stagesByType.qualifiers.map((group) => (
                      <div key={group.group_id}>{renderBracketRound(group)}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Playoffs */}
              {stagesByType.playoffs.length > 0 && (
                <div className="bracket-stage-column">
                  <div className="stage-column-header">
                    <h3>Playoffs</h3>
                    <span className="stage-format">Best of {stagesByType.playoffs[0]?.match_format || 3}</span>
                  </div>
                  <div className="stage-rounds">
                    {stagesByType.playoffs.map((group) => (
                      <div key={group.group_id}>{renderBracketRound(group)}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Semifinals */}
              {stagesByType.upperSemifinals.length > 0 && (
                <div className="bracket-stage-column">
                  <div className="stage-column-header">
                    <h3>Semifinals</h3>
                    <span className="stage-format">Best of {stagesByType.upperSemifinals[0]?.match_format || 3}</span>
                  </div>
                  <div className="stage-rounds">
                    {stagesByType.upperSemifinals.map((group) => (
                      <div key={group.group_id}>{renderBracketRound(group)}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Grand Finals */}
              {stagesByType.finals.length > 0 && (
                <div className="bracket-stage-column finals-column">
                  <div className="stage-column-header">
                    <h3>🏆 Grand Finals</h3>
                    <span className="stage-format">Best of {stagesByType.finals[0]?.match_format || 5}</span>
                  </div>
                  <div className="stage-rounds">
                    {stagesByType.finals.map((group) => (
                      <div key={group.group_id}>{renderBracketRound(group)}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bracket Modal */}
        {showBracketModal && selectedBracket && (
          <div className="bracket-modal-overlay" onClick={() => setShowBracketModal(false)}>
            <div className="bracket-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bracket-modal-header">
                <h2>{selectedBracket.group.group_name}</h2>
                <button className="modal-close-btn" onClick={() => setShowBracketModal(false)}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="bracket-modal-teams">
                <div className="modal-team">
                  {selectedBracket.team1?.logo && (
                    <img src={selectedBracket.team1.logo} alt={selectedBracket.team1.name} />
                  )}
                  <span>{selectedBracket.team1?.name || 'TBD'}</span>
                </div>
                <span className="modal-vs">VS</span>
                <div className="modal-team">
                  {selectedBracket.team2?.logo && (
                    <img src={selectedBracket.team2.logo} alt={selectedBracket.team2.name} />
                  )}
                  <span>{selectedBracket.team2?.name || 'TBD'}</span>
                </div>
              </div>
              <div className="bracket-modal-matches">
                <h3>Match History ({selectedBracket.matches.length} matches)</h3>
                {selectedBracket.matches.length > 0 ? (
                  <div className="modal-matches-list">
                    {selectedBracket.matches.map((match, idx) => (
                      <div key={match.match_id} className={`modal-match-card ${getMatchStatusClass(match.status)}`}>
                        <div className="modal-match-header">
                          <span className="modal-match-number">Match {idx + 1}</span>
                          <span className={`modal-match-status ${getMatchStatusClass(match.status)}`}>
                            {getMatchStatusText(match.status)}
                          </span>
                        </div>
                        <div className="modal-match-score">
                          <div
                            className={`modal-score-team ${
                              match.winner?.id === match.attacker_team?.id ? 'winner' : ''
                            }`}
                          >
                            <span className="modal-team-name">{match.attacker_team?.name}</span>
                            <span className="modal-team-score">{match.attacker_team?.score ?? '-'}</span>
                          </div>
                          <span className="modal-score-divider">-</span>
                          <div
                            className={`modal-score-team ${
                              match.winner?.id === match.defender_team?.id ? 'winner' : ''
                            }`}
                          >
                            <span className="modal-team-score">{match.defender_team?.score ?? '-'}</span>
                            <span className="modal-team-name">{match.defender_team?.name}</span>
                          </div>
                        </div>
                        {match.match_datetime && (
                          <div className="modal-match-datetime">
                            📅{' '}
                            {new Date(match.match_datetime).toLocaleString('en-US', {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })}
                          </div>
                        )}
                        {match.youtube_location && (
                          <a
                            href={match.youtube_location}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="modal-match-vod"
                          >
                            📺 Watch VOD
                          </a>
                        )}
                        {match.winner && <div className="modal-match-winner">🏆 Winner: {match.winner.name}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-matches">No matches played yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="fixtures-container">
        <div className="season-status-message">
          <h2>Loading...</h2>
          <p>Loading fixtures data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="fixtures-container">
        <div className="season-status-message">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Show no seasons state
  if (seasons.length === 0) {
    return (
      <div className="fixtures-container">
        <div className="season-status-message">
          <h2>No Seasons Available</h2>
          <p>There are no seasons configured yet. Please check back later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixtures-container">
      {/* Season Selection Tabs */}
      <div className="season-tabs-wrapper">
        <button className="scroll-arrow left" onClick={() => scroll(seasonTabsRef, 'left')} aria-label="Scroll left">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <div className="season-tabs" ref={seasonTabsRef}>
          {seasons.filter((season) =>{
           const seasonNumber = parseInt((season.name || '').replace('Season ', ''), 10);
           return seasonNumber >= 3;
          }).map((season) => (
            <div
              key={season.id}
              className={`season-tab ${activeSeason === season.id ? 'active' : ''}`}
              onClick={() => {
                setActiveSeason(season.id);
                // Update the store's current season
                useStore.getState().setCurrentSeason(season);
              }}
            >
              <span className="season-name">{season.name}</span>
            </div>
          ))}
        </div>
        <button className="scroll-arrow right" onClick={() => scroll(seasonTabsRef, 'right')} aria-label="Scroll right">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>

      {/* Stage Tabs - Direct replacement of View Tabs */}
      <div className="stage-tabs">
        <button
          className={`stage-tab ${activeView === 'group-stage' ? 'active' : ''}`}
          onClick={() => setActiveView('group-stage')}
        >
          Group Stages
        </button>
        <button
          className={`stage-tab ${activeView === 'timeline' ? 'active' : ''}`}
          onClick={() => setActiveView('timeline')}
        >
          Timeline
        </button>
        <button
          className={`stage-tab ${activeView === 'playoffs' ? 'active' : ''}`}
          onClick={() => setActiveView('playoffs')}
        >
          Playoffs
        </button>
      </div>

      {/* Fixtures Content */}
      <div className="fixtures-content">
        {fixturesLoading ? (
          <div className="loading-message">
            <h3>Loading fixtures...</h3>
          </div>
        ) : (
          <>
            {/* Render based on selected stage */}
            {activeView === 'group-stage' && renderGroupStage()}
            {activeView === 'timeline' && renderTimeline()}
            {activeView === 'playoffs' && renderPlayoffs()}
          </>
        )}
      </div>

      {/* Match Details Modal */}
      {showMatchModal && selectedMatch && (
        <div className="match-modal-overlay" onClick={() => setShowMatchModal(false)}>
          <div className="match-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setShowMatchModal(false)}>
              ✕
            </button>

            <div className="match-details-header">
              <h2>Match Details & Scorecard</h2>
              <div className="match-details-group">{selectedMatch.group_name}</div>
            </div>

            {/* Match Summary */}
            <div className="match-summary-card">
              <div className="match-summary-teams">
                <div className="match-summary-team">
                  {selectedMatch.attacker_team?.logo ? (
                    <img
                      src={selectedMatch.attacker_team.logo}
                      alt={selectedMatch.attacker_team.name}
                      className="match-summary-logo"
                    />
                  ) : (
                    <div className="match-summary-logo-placeholder">
                      {selectedMatch.attacker_team?.name?.charAt(0) || 'A'}
                    </div>
                  )}
                  <h3>{selectedMatch.attacker_team?.name || 'TBD'}</h3>
                </div>

                <div className="match-summary-score">
                  <div className="score-display">
                    <span className="final-score">{selectedMatch.attacker_team?.score ?? '-'}</span>
                    <span className="score-dash">:</span>
                    <span className="final-score">{selectedMatch.defender_team?.score ?? '-'}</span>
                  </div>
                  <div className="match-result">
                    {selectedMatch.winner ? (
                      <span className="winner-badge">🏆 {selectedMatch.winner.name} Won</span>
                    ) : (
                      <span className="draw-badge">Draw</span>
                    )}
                  </div>
                </div>

                <div className="match-summary-team">
                  <h3>{selectedMatch.defender_team?.name || 'TBD'}</h3>
                  {selectedMatch.defender_team?.logo ? (
                    <img
                      src={selectedMatch.defender_team.logo}
                      alt={selectedMatch.defender_team.name}
                      className="match-summary-logo"
                    />
                  ) : (
                    <div className="match-summary-logo-placeholder">
                      {selectedMatch.defender_team?.name?.charAt(0) || 'D'}
                    </div>
                  )}
                </div>
              </div>

              <div className="match-meta-info">
                {selectedMatch.match_datetime && (
                  <p>
                    <strong>Date & Time:</strong>{' '}
                    {new Date(selectedMatch.match_datetime).toLocaleString('en-US', {
                      dateStyle: 'full',
                      timeStyle: 'short',
                    })}
                  </p>
                )}
                {selectedMatch.venue && (
                  <p>
                    <strong>Venue:</strong> {selectedMatch.venue}{' '}
                    {selectedMatch.venue_location && `(${selectedMatch.venue_location})`}
                  </p>
                )}
                {selectedMatch.youtube_location && (
                  <a
                    href={selectedMatch.youtube_location}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="match-video-link"
                  >
                    ▶️ Watch Full Match on YouTube
                  </a>
                )}
              </div>
            </div>

            {/* Match Scoreboard (replaces Map Breakdown) */}
            {loadingPlayerStats ? (
              <div className="players-scorecard-section">
                <h3>Match Scoreboard</h3>
                <div className="loading-players">Loading player statistics...</div>
              </div>
            ) : matchPlayerStats ? (
              <div className="players-scorecard-section">
                <h3>Match Scoreboard</h3>
                <div className="scorecard-tables">
                  {/* Attacker Team */}
                  <div className="team-scorecard">
                    <div className="team-scorecard-header">
                      {matchPlayerStats.attacker_team?.logo ? (
                        <img
                          src={matchPlayerStats.attacker_team.logo}
                          alt={matchPlayerStats.attacker_team.name}
                          className="team-scorecard-logo"
                        />
                      ) : (
                        <div className="team-scorecard-logo-placeholder">
                          {matchPlayerStats.attacker_team?.name?.charAt(0) || 'A'}
                        </div>
                      )}
                      <h4>
                        {matchPlayerStats.attacker_team?.name} ({matchPlayerStats.attacker_team?.score})
                      </h4>
                    </div>
                    <table className="players-table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>K</th>
                          <th>D</th>
                          <th>A</th>
                          <th>K/D</th>
                          <th>HSP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchPlayerStats.attacker_team?.players?.map((player, idx) => (
                          <tr key={idx}>
                            <td className="player-name-cell">
                              {player.peak_rank && player.peak_rank !== 'N/A' && player.peak_rank !== 'Unranked' && (
                                <img
                                  src={`/images/ranks/${player.peak_rank.toLowerCase().replace(/\s+/g, '_')}.png`}
                                  alt={player.peak_rank}
                                  className="rank-icon"
                                  title={`Peak Rank: ${player.peak_rank}`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}
                              <span className="player-name">{player.player_name}</span>
                            </td>
                            <td className="kills-cell">{player.kills}</td>
                            <td className="deaths-cell">{player.deaths}</td>
                            <td className="assists-cell">{player.assists}</td>
                            <td className="kd-cell">{player.kd}</td>
                            <td className="headshots-cell">{player.headshotpercentage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Defender Team */}
                  <div className="team-scorecard">
                    <div className="team-scorecard-header">
                      {matchPlayerStats.defender_team?.logo ? (
                        <img
                          src={matchPlayerStats.defender_team.logo}
                          alt={matchPlayerStats.defender_team.name}
                          className="team-scorecard-logo"
                        />
                      ) : (
                        <div className="team-scorecard-logo-placeholder">
                          {matchPlayerStats.defender_team?.name?.charAt(0) || 'D'}
                        </div>
                      )}
                      <h4>
                        {matchPlayerStats.defender_team?.name} ({matchPlayerStats.defender_team?.score})
                      </h4>
                    </div>
                    <table className="players-table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>K</th>
                          <th>D</th>
                          <th>A</th>
                          <th>K/D</th>
                          <th>HSP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchPlayerStats.defender_team?.players?.map((player, idx) => (
                          <tr key={idx}>
                            <td className="player-name-cell">
                              {player.peak_rank && player.peak_rank !== 'N/A' && player.peak_rank !== 'Unranked' && (
                                <img
                                  src={`/images/ranks/${player.peak_rank.toLowerCase().replace(/\s+/g, '_')}.png`}
                                  alt={player.peak_rank}
                                  className="rank-icon"
                                  title={`Peak Rank: ${player.peak_rank}`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              )}
                              <span className="player-name">{player.player_name}</span>
                            </td>
                            <td className="kills-cell">{player.kills}</td>
                            <td className="deaths-cell">{player.deaths}</td>
                            <td className="assists-cell">{player.assists}</td>
                            <td className="kd-cell">{player.kd}</td>
                            <td className="headshots-cell">{player.headshotpercentage}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Match Statistics */}
            <div className="match-stats-section">
              <h3>Match Statistics</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-label">Attacker Rounds Won</span>
                  <span className="stat-value">{selectedMatch.attacker_team?.score ?? 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Defender Rounds Won</span>
                  <span className="stat-value">{selectedMatch.defender_team?.score ?? 0}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Rounds</span>
                  <span className="stat-value">
                    {(selectedMatch.attacker_team?.score ?? 0) + (selectedMatch.defender_team?.score ?? 0)}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Match Status</span>
                  <span className="stat-value">Completed</span>
                </div>
              </div>
            </div>

            {/* Duplicate players scoreboard removed (kept the primary Match Scoreboard above) */}
          </div>
        </div>
      )}
    </div>
  );
}

export default Fixtures;